import { SAFEGUARDING_ITEM_ID } from '@lmc/common/types'
import type { GoalListResponse, LogCategoryItem } from '@lmc/ts-common'
import { DatePicker, FormItem, Input, TextArea } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Checkbox, Select, TimePicker } from 'antd'
import type dayjs from 'dayjs'
import type { IncidentTypeItem } from 'hooks/useIncidentTypeOptions'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'

type IncidentLogFormFieldsProps = {
  isCase?: boolean
  locations: string[] | undefined | null
  goals: GoalListResponse[] | undefined
  incidentTypes: IncidentTypeItem[]
  hasLocationsEnabled: boolean
  item?: LogCategoryItem
  testId?: string
}

const IncidentLogFormFields = ({
  isCase,
  locations,
  goals,
  incidentTypes,
  hasLocationsEnabled,
  item,
  testId = 'incident-log-form-fields'
}: IncidentLogFormFieldsProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  const goalsOptions = goals?.map((goal) => ({
    value: goal.id,
    label: goal.title
  }))

  const locationsOptions = locations?.map((location) => ({
    value: location,
    label: location
  }))

  const isSafeguarding = item?.id === SAFEGUARDING_ITEM_ID

  return (
    <>
      <FormItem
        data-testid={`${testId}-incident-type-field`}
        name={isCase ? ['log', 'item'] : ['item']}
        label={<Text>{t(incidentReportsConstants.INCIDENT_TYPES)}</Text>}
      >
        <Select
          data-testid={`${testId}-incident-type-select`}
          optionRender={(option) => <Text>{option.label}</Text>}
          options={incidentTypes}
        />
      </FormItem>
      <FormItem
        data-testid={`${testId}-title-field`}
        name={isCase ? ['log', 'title'] : 'title'}
        label={<Text>{t(incidentReportsConstants.TITLE)}</Text>}
      >
        <Input data-testid={`${testId}-title-input`} maxLength={35} />
      </FormItem>
      <FormItem
        data-testid={`${testId}-description-field`}
        name={isCase ? ['log', 'description'] : 'description'}
        label={<Text>{t(incidentReportsConstants.DESCRIPTION)}</Text>}
      >
        <TextArea
          data-testid={`${testId}-description-textarea`}
          autoSize={{
            minRows: 3
          }}
        />
      </FormItem>
      <FormItem
        data-testid={`${testId}-date-logged-field`}
        name={isCase ? ['log', 'dateLogged'] : 'dateLogged'}
        label={<Text>{t(incidentReportsConstants.DATE_LOGGED)}</Text>}
      >
        <DatePicker data-testid={`${testId}-date-logged-picker`} />
      </FormItem>
      <FormItem
        data-testid={`${testId}-time-logged-field`}
        name={isCase ? ['log', 'timeLogged'] : 'timeLogged'}
        label={<Text>{t(incidentReportsConstants.TIME_LOGGED)}</Text>}
      >
        <TimePicker data-testid={`${testId}-time-logged-picker`} />
      </FormItem>
      {hasLocationsEnabled && (
        <FormItem
          data-testid={`${testId}-location-field`}
          name={isCase ? ['log', 'location'] : 'location'}
          label={<Text>{t(incidentReportsConstants.LOCATION)}</Text>}
        >
          <Select
            data-testid={`${testId}-location-select`}
            optionRender={(option) => <Text>{option.label}</Text>}
            options={locationsOptions}
          />
        </FormItem>
      )}
      {!isSafeguarding && (
        <>
          <FormItem
            data-testid={`${testId}-goals-field`}
            name={isCase ? ['log', 'goals'] : 'goals'}
            label={<Text>{t(incidentReportsConstants.LINKED_GOALS)}</Text>}
          >
            <Select
              data-testid={`${testId}-goals-select`}
              optionRender={(option) => <Text>{option.label}</Text>}
              options={goalsOptions}
              allowClear
              mode="multiple"
            />
          </FormItem>
          <FormItem
            data-testid={`${testId}-handover-field`}
            valuePropName="checked"
            name={isCase ? ['log', 'isNextHandover'] : 'isNextHandover'}
            label={<Text>{t(incidentReportsConstants.HANDOVER)}</Text>}
          >
            <Checkbox data-testid={`${testId}-handover-checkbox`}>
              <Text>{t(incidentReportsConstants.FLAG_FOR_HANDOVER)}</Text>
            </Checkbox>
          </FormItem>
        </>
      )}
    </>
  )
}

export type IncidentReportsLogFields<T> = {
  goals: string[]
  dateLogged: dayjs.Dayjs
  timeLogged: dayjs.Dayjs
  item?: T
}

export default IncidentLogFormFields
